import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  useUser, 
  useAction 
} from '@gadgetinc/react';
import { 
  Box, 
  Table, 
  Thead, 
  Tbody, 
  Tr, 
  Th, 
  Td, 
  Button, 
  Input, 
  Select, 
  Badge, 
  IconButton,
  Flex,
  Heading,
  Text,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  InputGroup,
  InputLeftElement,
  HStack,
  VStack,
  Card,
  CardBody,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Textarea,
  useDisclosure,
  Checkbox
} from '@chakra-ui/react';
import { 
  SearchIcon, 
  AddIcon, 
  LockIcon, 
  UnlockIcon, 
  EditIcon,
  ChevronDownIcon,
  QuestionIcon,
  HamburgerIcon
} from '@chakra-ui/icons';
import { MdArrowBack } from 'react-icons/md';
import { api } from '../api';
import { hasRole, normalizeInstitutionRoles } from '../lib/roleUtils';

const PROCESSING_FINAL_STATUSES = ['analysis_complete', 'requires_review'];
const PROCESSING_FINAL_EVALUATION_STATUSES = ['completed'];

const parseJsonSafely = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (error) {
    return null;
  }
};

const toDateKeyUTC = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().slice(0, 10);
};

const getDateKeyCandidates = (value) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return [];
  const dayMs = 24 * 60 * 60 * 1000;
  return [
    new Date(parsed.getTime() - dayMs).toISOString().slice(0, 10),
    parsed.toISOString().slice(0, 10),
    new Date(parsed.getTime() + dayMs).toISOString().slice(0, 10),
  ];
};

const Shifts = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const user = useUser();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [shiftTypeFilter, setShiftTypeFilter] = useState({
    clinical: true,
    field: true,
    lab: false,
  });
  const [sortBy, setSortBy] = useState('shiftDate');
  const [sortOrder, setSortOrder] = useState('Descending');
  const [selectedShift, setSelectedShift] = useState(null);
  const [unlockReason, setUnlockReason] = useState('');
  const [shifts, setShifts] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [processingPatientCountLookups, setProcessingPatientCountLookups] = useState({
    bySourceShiftId: {},
    byScheduledShiftId: {},
    byStudentDate: {},
  });
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [{ data: updateResult, fetching: isUpdating }, updateShift] = useAction(api.shift.update);

  // Determine user role and build filter
  const { filter, userRole } = useMemo(() => {
    if (!user) return { filter: {}, userRole: 'student' };
    
    const institutionRoles = normalizeInstitutionRoles(user.institutionRole);
    const isInstitutionAdmin = institutionRoles.includes("administrator");
    const isInstructor = institutionRoles.includes("instructor");
    
    let role = 'student';
    let shiftFilter = {};
    
    if (hasRole(user.roles || user.roleList, 'admin') || isInstitutionAdmin) {
      role = 'admin';
      shiftFilter = {
        institution: { equals: user.institution }
      };
    } else {
      const activeCohortIds = Array.isArray(user.activeCohorts)
        ? user.activeCohorts.filter(Boolean)
        : [];
      const primaryCohortId = user.cohort || null;
      const cohortIds = [...new Set([primaryCohortId, ...activeCohortIds].filter(Boolean))];

      role = isInstructor ? 'instructor' : 'student';

      shiftFilter = {
        institution: { equals: user.institution }
      };

      if (cohortIds.length === 1) {
        shiftFilter.cohortId = { equals: cohortIds[0] };
      } else if (cohortIds.length > 1) {
        shiftFilter.cohortId = { in: cohortIds };
      }
    }
    
    // Add status filter
    if (statusFilter) {
      shiftFilter.status = { equals: statusFilter };
    }
    
    return { filter: shiftFilter, userRole: role };
  }, [user, statusFilter]);

  const refetch = useCallback(() => {
    setReloadNonce((prev) => prev + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadAllShifts = async () => {
      if (!user) {
        if (!cancelled) {
          setShifts([]);
          setFetching(false);
          setError(null);
        }
        return;
      }

      setFetching(true);
      setError(null);

      try {
        const allShifts = [];
        let after;

        while (true) {
          const page = await api.shift.findMany({
            filter,
            sort: { [sortBy]: sortOrder },
            first: 250,
            after,
            select: {
              id: true,
              shiftDate: true,
              location: true,
              unit: true,
              shiftType: true,
              status: true,
              actualPatients: true,
              locked: true,
              institution: true,
              cohortId: true,
              fisdapEventUid: true,
              cohort: { id: true, calendarMode: true, displayName: true },
              preceptor: {
                id: true,
                firstName: true,
                lastName: true,
              },
              createdAt: true,
              updatedAt: true,
            },
          });

          allShifts.push(...page);

          if (!page.hasNextPage || !page.endCursor) break;
          after = page.endCursor;
        }

        if (!cancelled) {
          setShifts(allShifts);
        }
      } catch (loadError) {
        if (!cancelled) {
          setShifts([]);
          setError(loadError);
        }
      } finally {
        if (!cancelled) {
          setFetching(false);
        }
      }
    };

    loadAllShifts();

    return () => {
      cancelled = true;
    };
  }, [filter, sortBy, sortOrder, reloadNonce, user]);

  const visibleShiftIds = useMemo(() => (shifts || []).map((shift) => shift.id).filter(Boolean), [shifts]);
  const [shiftDocs, setShiftDocs] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const loadShiftDocs = async () => {
      if (visibleShiftIds.length === 0) {
        if (!cancelled) setShiftDocs([]);
        return;
      }

      try {
        const allDocs = [];
        const chunkSize = 150;

        for (let index = 0; index < visibleShiftIds.length; index += chunkSize) {
          const chunk = visibleShiftIds.slice(index, index + chunkSize);
          let after;

          while (true) {
            const page = await api.shiftDocumentation.findMany({
              filter: {
                shiftId: { in: chunk },
              },
              first: 250,
              after,
              select: {
                id: true,
                shiftId: true,
                studentId: true,
                student: {
                  id: true,
                  firstName: true,
                  lastName: true,
                  email: true,
                },
              },
            });

            allDocs.push(...page);

            if (!page.hasNextPage || !page.endCursor) break;
            after = page.endCursor;
          }
        }

        if (!cancelled) {
          setShiftDocs(allDocs);
        }
      } catch (loadError) {
        console.error("Failed to load shift documentation pages", loadError);
        if (!cancelled) {
          setShiftDocs([]);
          toast({
            title: "Error loading assigned students",
            description: loadError.message || "Unable to load shift documentation",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      }
    };

    loadShiftDocs();

    return () => {
      cancelled = true;
    };
  }, [visibleShiftIds, toast]);

  const assignedStudentsByShiftId = useMemo(() => {
    const grouped = new Map();
    (shiftDocs || []).forEach((doc) => {
      if (!doc?.shiftId || !doc?.student?.id) return;
      if (!grouped.has(doc.shiftId)) grouped.set(doc.shiftId, []);
      const current = grouped.get(doc.shiftId);
      if (!current.some((student) => student.id === doc.student.id)) {
        current.push({
          id: doc.student.id,
          name: `${doc.student.firstName || ''} ${doc.student.lastName || ''}`.trim() || doc.student.email || 'Unknown',
          email: doc.student.email || '',
        });
      }
    });
    return grouped;
  }, [shiftDocs]);

  const availableStudents = useMemo(() => {
    const seen = new Map();
    Array.from(assignedStudentsByShiftId.values()).flat().forEach((student) => {
      if (!seen.has(student.id)) seen.set(student.id, student);
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [assignedStudentsByShiftId]);

  useEffect(() => {
    let cancelled = false;

    const loadFallbackPatientCounts = async () => {
      const sourceShiftIds = Array.from(
        new Set(
          (shifts || [])
            .map((shift) => String(shift?.fisdapEventUid || '').trim())
            .filter(Boolean)
        )
      );

      const assignedStudentIds = Array.from(
        new Set(
          Array.from(assignedStudentsByShiftId.values())
            .flat()
            .map((student) => String(student?.id || '').trim())
            .filter(Boolean)
        )
      );

      if (sourceShiftIds.length === 0 && assignedStudentIds.length === 0) {
        if (!cancelled) {
          setProcessingPatientCountLookups({
            bySourceShiftId: {},
            byScheduledShiftId: {},
            byStudentDate: {},
          });
        }
        return;
      }

      try {
        const recordsById = new Map();
        const addRecord = (record) => {
          const key = String(record?.id || '');
          if (key) recordsById.set(key, record);
        };

        const fetchRecords = async (filter) => {
          let after;
          while (true) {
            const page = await api.shiftProcessing.findMany({
              filter,
              first: 250,
              after,
              select: {
                id: true,
                sourceShiftId: true,
                submittedById: true,
                patientEncounterCount: true,
                processingStatus: true,
                evaluationStatus: true,
                aiEvaluationMetadata: true,
                structuredData: true,
                submissionDate: true,
                updatedAt: true,
                createdAt: true,
              },
            });

            page.forEach(addRecord);
            if (!page.hasNextPage || !page.endCursor) break;
            after = page.endCursor;
          }
        };

        const validStateFilter = {
          OR: [
            { processingStatus: { in: PROCESSING_FINAL_STATUSES } },
            { evaluationStatus: { in: PROCESSING_FINAL_EVALUATION_STATUSES } },
          ],
          isDraft: { equals: false },
        };

        const sourceChunkSize = 150;
        for (let index = 0; index < sourceShiftIds.length; index += sourceChunkSize) {
          const chunk = sourceShiftIds.slice(index, index + sourceChunkSize);
          await fetchRecords({
            ...validStateFilter,
            sourceShiftId: { in: chunk },
          });
        }

        const shiftDates = (shifts || [])
          .map((shift) => new Date(shift?.shiftDate))
          .filter((date) => !Number.isNaN(date.getTime()));
        let minShiftTime = null;
        let maxShiftTime = null;
        shiftDates.forEach((date) => {
          const time = date.getTime();
          if (minShiftTime == null || time < minShiftTime) minShiftTime = time;
          if (maxShiftTime == null || time > maxShiftTime) maxShiftTime = time;
        });

        const dateRangeFilter = {};
        if (minShiftTime != null && maxShiftTime != null) {
          const dayMs = 24 * 60 * 60 * 1000;
          dateRangeFilter.submissionDate = {
            greaterThanOrEqual: new Date(minShiftTime - (3 * dayMs)),
            lessThanOrEqual: new Date(maxShiftTime + (3 * dayMs)),
          };
        }

        const studentChunkSize = 120;
        for (let index = 0; index < assignedStudentIds.length; index += studentChunkSize) {
          const chunk = assignedStudentIds.slice(index, index + studentChunkSize);
          await fetchRecords({
            ...validStateFilter,
            submittedById: { in: chunk },
            ...dateRangeFilter,
          });
        }

        const latestBySourceShiftId = new Map();
        const latestByScheduledShiftId = new Map();
        const latestByStudentDate = new Map();

        const upsertLatest = (map, key, count, timestamp) => {
          if (!key) return;
          const next = Number.isFinite(count) && count > 0 ? count : 0;
          const ts = Number.isFinite(timestamp) ? timestamp : 0;
          const existing = map.get(key);
          if (!existing || ts > existing.timestamp || (ts === existing.timestamp && next > existing.count)) {
            map.set(key, { count: next, timestamp: ts });
          }
        };

        recordsById.forEach((record) => {
          const timestamp = new Date(
            record?.submissionDate || record?.updatedAt || record?.createdAt || 0
          ).getTime();
          const count = Number(record?.patientEncounterCount || 0);
          const sourceShiftId = String(record?.sourceShiftId || '').trim();
          upsertLatest(latestBySourceShiftId, sourceShiftId, count, timestamp);

          const metadata = parseJsonSafely(record?.aiEvaluationMetadata) || {};
          const scheduledShiftId = String(
            metadata?.scheduledShiftLink?.shiftId ||
            metadata?.scheduledShiftId ||
            metadata?.scheduledShift?.id ||
            ''
          ).trim();
          upsertLatest(latestByScheduledShiftId, scheduledShiftId, count, timestamp);

          const studentId = String(record?.submittedById || '').trim();
          const structuredData = parseJsonSafely(record?.structuredData) || {};
          const structuredShiftDate =
            structuredData?.shift?.shiftDate ||
            structuredData?.shift?.shift_date ||
            structuredData?.shift?.document_info?.shift_date ||
            structuredData?.document_info?.shift_date ||
            structuredData?.shift_date ||
            null;

          const dateKey = toDateKeyUTC(structuredShiftDate);
          if (studentId && dateKey) {
            upsertLatest(latestByStudentDate, `${studentId}:${dateKey}`, count, timestamp);
          }
        });

        if (!cancelled) {
          const serialize = (map) => {
            const obj = {};
            map.forEach((value, key) => {
              obj[key] = Number(value?.count || 0);
            });
            return obj;
          };

          setProcessingPatientCountLookups({
            bySourceShiftId: serialize(latestBySourceShiftId),
            byScheduledShiftId: serialize(latestByScheduledShiftId),
            byStudentDate: serialize(latestByStudentDate),
          });
        }
      } catch (countError) {
        console.warn('Failed to load fallback shift patient counts', countError);
        if (!cancelled) {
          setProcessingPatientCountLookups({
            bySourceShiftId: {},
            byScheduledShiftId: {},
            byStudentDate: {},
          });
        }
      }
    };

    loadFallbackPatientCounts();

    return () => {
      cancelled = true;
    };
  }, [shifts, assignedStudentsByShiftId]);

  const normalizeShiftType = (shiftType) => {
    const normalized = String(shiftType || "").trim().toLowerCase();
    if (normalized.includes("clinical")) return "clinical";
    if (normalized.includes("field")) return "field";
    if (normalized.includes("lab")) return "lab";
    return "other";
  };

  // Filter shifts based on search term
  const filteredShifts = useMemo(() => {
    if (!shifts) return shifts;

    return shifts.filter((shift) => {
      const assignedStudents = assignedStudentsByShiftId.get(shift.id) || [];
      const search = searchTerm.trim().toLowerCase();
      const normalizedShiftType = normalizeShiftType(shift.shiftType);
      const matchesSearch = !search || (
        shift.location?.toLowerCase().includes(search) ||
        shift.unit?.toLowerCase().includes(search) ||
        shift.institution?.toLowerCase().includes(search) ||
        shift.cohort?.displayName?.toLowerCase().includes(search) ||
        assignedStudents.some((student) => student.name.toLowerCase().includes(search) || student.email.toLowerCase().includes(search))
      );
      const matchesStudent = !selectedStudentId || assignedStudents.some((student) => student.id === selectedStudentId);
      const matchesShiftType = normalizedShiftType === "other" ? true : Boolean(shiftTypeFilter[normalizedShiftType]);
      return matchesSearch && matchesStudent && matchesShiftType;
    });
  }, [shifts, searchTerm, selectedStudentId, assignedStudentsByShiftId, shiftTypeFilter]);

  // Check if any visible shifts belong to a sync-mode cohort
  const hasSyncModeCohort = useMemo(() => {
    if (!shifts) return false;
    return shifts.some(s => s.cohort?.calendarMode === "sync");
  }, [shifts]);

  const handleCreateShift = () => {
    navigate('/shifts/new');
  };

  const handleEditShift = (shift) => {
    if (!canEditShift(shift)) {
      toast({
        title: 'Cannot edit shift',
        description: 'This shift is locked or you do not have permission to edit it.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    navigate(`/shifts/${shift.id}/edit`);
  };

  const handleToggleLock = async (shift) => {
    const lockState = getShiftLockState(shift);
    
    // If student is trying to unlock a locked shift, show request modal
    if (userRole === 'student' && lockState.isLocked && getShiftPhase(shift.shiftDate) !== 'active') {
      setSelectedShift(shift);
      setUnlockReason('');
      onOpen();
      return;
    }
    
    // If student is trying to lock their own shift, show confirmation
    if (userRole === 'student') {
      const confirmed = window.confirm(
        'Are you sure you want to lock this shift? Once locked, you will need to request unlock permission from your instructor to make changes.'
      );
      if (!confirmed) return;
    }
    
    if (!lockState.canToggle) {
      toast({
        title: 'Cannot toggle lock',
        description: lockState.reason,
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await updateShift({
        id: shift.id,
        locked: !lockState.isLocked,
      });
      
      toast({
        title: lockState.isLocked ? 'Shift unlocked' : 'Shift locked',
        description: `Shift has been ${lockState.isLocked ? 'unlocked' : 'locked'} successfully.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      refetch();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update shift lock status.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled':
        return 'blue';
      case 'in_progress':
        return 'yellow';
      case 'completed':
        return 'green';
      case 'cancelled':
        return 'red';
      default:
        return 'gray';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getShiftPhase = (shiftDate) => {
    if (!shiftDate) return 'future';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const shift = new Date(shiftDate);
    shift.setHours(0, 0, 0, 0);
    
    if (shift > today) return 'future';
    if (shift.getTime() === today.getTime()) return 'active';
    return 'past';
  };

  const getShiftLockState = (shift) => {
    const phase = getShiftPhase(shift.shiftDate);
    
    // Pre-shift: can be locked/unlocked by instructors
    if (phase === 'future') {
      return {
        isLocked: shift.locked,
        canToggle: userRole !== 'student',
        reason: shift.locked ? 'Shift is locked by instructor' : 'Shift is unlocked'
      };
    }
    
    // Active shift: should be unlocked for data entry
    if (phase === 'active') {
      return {
        isLocked: false,
        canToggle: userRole !== 'student',
        reason: 'Active shift - unlocked for data entry'
      };
    }
    
    // Past shift: instructors/schedulers/admins can still toggle, students cannot
    return {
      isLocked: true,
      canToggle: userRole !== 'student',
      reason: userRole === 'student' ? 'Past shift - auto-locked to preserve data' : 'Past shift - can be unlocked by instructor/admin'
    };
  };

  const canAddPatientEncounter = (shift) => {
    const phase = getShiftPhase(shift.shiftDate);
    const lockState = getShiftLockState(shift);
    
    // Don't allow if locked (except for active shifts)
    if (lockState.isLocked && phase !== 'active') return false;
    
    // Students can only add to their own shifts
    if (userRole === 'student') {
      return false;
    }
    
    // Instructors and admins can add to any shift
    return true;
  };

  const canViewPatientList = (shift) => {
    return getEffectivePatientCount(shift) > 0;
  };

  const getEffectivePatientCount = useCallback((shift) => {
    const actualPatients = Number(shift?.actualPatients || 0);
    if (actualPatients > 0) {
      return actualPatients;
    }

    const byScheduledShiftId = processingPatientCountLookups?.byScheduledShiftId || {};
    const byStudentDate = processingPatientCountLookups?.byStudentDate || {};
    const bySourceShiftId = processingPatientCountLookups?.bySourceShiftId || {};

    const scheduledShiftFallback = Number(byScheduledShiftId[String(shift?.id || '').trim()] || 0);
    if (scheduledShiftFallback > 0) {
      return scheduledShiftFallback;
    }

    const dateKeyCandidates = getDateKeyCandidates(shift?.shiftDate);
    const assignedStudents = assignedStudentsByShiftId.get(shift.id) || [];
    let studentDateFallback = 0;

    if (selectedStudentId && dateKeyCandidates.length > 0) {
      dateKeyCandidates.forEach((dateKey) => {
        const key = `${String(selectedStudentId)}:${dateKey}`;
        const count = Number(byStudentDate[key] || 0);
        if (count > studentDateFallback) studentDateFallback = count;
      });
    } else if (assignedStudents.length > 0 && dateKeyCandidates.length > 0) {
      assignedStudents.forEach((student) => {
        const studentId = String(student?.id || '').trim();
        if (!studentId) return;
        dateKeyCandidates.forEach((dateKey) => {
          const key = `${studentId}:${dateKey}`;
          const count = Number(byStudentDate[key] || 0);
          if (count > studentDateFallback) studentDateFallback = count;
        });
      });
    }

    if (studentDateFallback > 0) {
      return studentDateFallback;
    }

    const sourceShiftId = String(shift?.fisdapEventUid || "").trim();
    if (!sourceShiftId) {
      return actualPatients;
    }

    const fallbackCount = Number(bySourceShiftId[sourceShiftId] || 0);
    return fallbackCount > 0 ? fallbackCount : actualPatients;
  }, [processingPatientCountLookups, assignedStudentsByShiftId, selectedStudentId]);

  const canEditShift = (shift) => {
    const lockState = getShiftLockState(shift);
    
    // Student shift assignment is no longer on shift records; avoid broad edit access.
    if (userRole === 'student') return false;
    
    // Admins can edit any shift
    if (userRole === 'admin') return true;
    
    // Instructors can edit if not auto-locked (past shifts)
    return lockState.canToggle;
  };

  const handleAddPatientEncounter = (shift) => {
    navigate(`/patient-encounters?shiftId=${shift.id}`);
  };

  const handleViewPatientList = (shift) => {
    navigate(`/shifts/${shift.id}/patients`);
  };

  const handleRequestEdit = (shift) => {
    // For now, just navigate to edit. In the future, this could trigger a request flow
    navigate(`/shifts/${shift.id}/edit`);
  };

  const handleSubmitUnlockRequest = async () => {
    if (!unlockReason.trim()) {
      toast({
        title: 'Reason required',
        description: 'Please provide a reason for the unlock request.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // For now, we'll just show a success message
      // In the future, this would call an API to create a shift unlock request
      toast({
        title: 'Unlock request submitted',
        description: 'Your unlock request has been submitted to your instructor for review.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      onClose();
      setSelectedShift(null);
      setUnlockReason('');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to submit unlock request. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCloseModal = () => {
    onClose();
    setSelectedShift(null);
    setUnlockReason('');
  };

  if (fetching && shifts.length === 0) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" color="blue.500" />
        <Text mt={4} color="gray.400">Loading shifts...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={8}>
        <Alert status="error" bg="red.900" color="white">
          <AlertIcon />
          Error loading shifts: {error.toString()}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={8} bg="gray.900" minH="100vh" color="white">
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
          <Button
            leftIcon={<MdArrowBack />}
            variant="ghost"
            colorScheme="gray"
            onClick={() => navigate('/')}
            size="md"
          >
            Back to Dashboard
          </Button>
          <Heading size="lg" color="white">
            Scheduled Shifts Dashboard
          </Heading>
          <Button
            variant="outline"
            colorScheme="purple"
            onClick={() => navigate('/shift-compliance')}
            size="md"
          >
            Search Documentation by Student
          </Button>
          <Button
            variant="outline"
            colorScheme="cyan"
            onClick={() => navigate('/admin/calendar-sync')}
            size="md"
          >
            Calendar Sync
          </Button>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={handleCreateShift}
            size="lg"
            isDisabled={hasSyncModeCohort}
            title={hasSyncModeCohort ? "Shifts are managed by FISDAP calendar sync" : undefined}
          >
            Create New Shift
          </Button>
        </Flex>

        {hasSyncModeCohort && (
          <Alert status="info" borderRadius="md" bg="blue.900" borderColor="blue.600" mb={4}>
            <AlertIcon color="blue.300" />
            <Text fontSize="sm" color="blue.100">
              This cohort uses FISDAP calendar sync. Shifts are managed automatically and cannot be manually created, edited, or deleted.
            </Text>
          </Alert>
        )}

        {/* Filters */}
        <Card bg="gray.800" borderColor="gray.700">
          <CardBody>
            <HStack spacing={4} wrap="wrap">
              <Box minW="300px">
                <InputGroup>
                  <InputLeftElement>
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search shifts, sites, or assigned students..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    bg="gray.700"
                    border="none"
                    color="white"
                    _placeholder={{ color: 'gray.400' }}
                  />
                </InputGroup>
              </Box>
              
              <Select
                placeholder="All Statuses"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                bg="gray.700"
                border="none"
                color="white"
                w="200px"
              >
                <option value="scheduled">Scheduled</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </Select>

              <Select
                placeholder="Filter by assigned student"
                value={selectedStudentId}
                onChange={(e) => setSelectedStudentId(e.target.value)}
                bg="gray.700"
                border="none"
                color="white"
                w="260px"
              >
                {availableStudents.map((student) => (
                  <option key={student.id} value={student.id}>
                    {student.name}
                  </option>
                ))}
              </Select>

              <Box
                bg="gray.700"
                borderRadius="md"
                px={4}
                py={2}
                minW="320px"
              >
                <Text fontSize="xs" color="gray.300" mb={2} textTransform="uppercase" letterSpacing="0.08em">
                  Shift Types
                </Text>
                <HStack spacing={4} wrap="wrap">
                  <Checkbox
                    colorScheme="green"
                    isChecked={shiftTypeFilter.clinical}
                    onChange={(e) => setShiftTypeFilter((prev) => ({ ...prev, clinical: e.target.checked }))}
                  >
                    Clinical
                  </Checkbox>
                  <Checkbox
                    colorScheme="orange"
                    isChecked={shiftTypeFilter.field}
                    onChange={(e) => setShiftTypeFilter((prev) => ({ ...prev, field: e.target.checked }))}
                  >
                    Field
                  </Checkbox>
                  <Checkbox
                    colorScheme="blue"
                    isChecked={shiftTypeFilter.lab}
                    onChange={(e) => setShiftTypeFilter((prev) => ({ ...prev, lab: e.target.checked }))}
                  >
                    Lab
                  </Checkbox>
                </HStack>
              </Box>

              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                bg="gray.700"
                border="none"
                color="white"
                w="150px"
              >
                <option value="shiftDate">Date</option>
                <option value="location">Location</option>
                <option value="status">Status</option>
                <option value="createdAt">Created</option>
              </Select>

              <Select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                bg="gray.700"
                border="none"
                color="white"
                w="120px"
              >
                <option value="Descending">Desc</option>
                <option value="Ascending">Asc</option>
              </Select>
            </HStack>
          </CardBody>
        </Card>

        {/* Shifts Table */}
        <Card bg="gray.800" borderColor="gray.700">
          <CardBody p={0}>
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead bg="gray.700">
                  <Tr>
                    <Th color="gray.300" border="none">Date</Th>
                    <Th color="gray.300" border="none">Location</Th>
                    <Th color="gray.300" border="none">Unit</Th>
                    <Th color="gray.300" border="none">Type</Th>
                    <Th color="gray.300" border="none">Status</Th>
                    <Th color="gray.300" border="none">Assigned Students</Th>
                    <Th color="gray.300" border="none">Patient Count</Th>
                    {userRole === 'admin' && <Th color="gray.300" border="none">Institution</Th>}
                    {userRole === 'admin' && <Th color="gray.300" border="none">Cohort</Th>}
                    <Th color="gray.300" border="none">Lock</Th>
                    <Th color="gray.300" border="none">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredShifts?.map((shift) => {
                    const effectivePatientCount = getEffectivePatientCount(shift);
                    return (
                    <Tr 
                      key={shift.id} 
                      _hover={{ bg: 'gray.700' }}
                    >
                      <Td border="none" color="gray.300">
                        {formatDate(shift.shiftDate)}
                      </Td>
                      <Td border="none" color="gray.300">
                        {shift.location}
                      </Td>
                      <Td border="none" color="gray.300">
                        {shift.unit}
                      </Td>
                      <Td border="none" color="gray.300">
                        {shift.shiftType}
                      </Td>
                      <Td border="none">
                        <Badge 
                          colorScheme={getStatusColor(shift.status)}
                          variant="solid"
                        >
                          {shift.status?.replace('_', ' ')}
                        </Badge>
                      </Td>
                      <Td border="none" color="gray.300">
                        <VStack align="start" spacing={1}>
                          {(assignedStudentsByShiftId.get(shift.id) || []).length > 0 ? (
                            (assignedStudentsByShiftId.get(shift.id) || []).map((student) => (
                              <Badge key={student.id} colorScheme="purple" variant="subtle">
                                {student.name}
                              </Badge>
                            ))
                          ) : (
                            <Text color="gray.500">None assigned</Text>
                          )}
                        </VStack>
                      </Td>
                      <Td border="none" color="gray.300">
                        <HStack spacing={2}>
                          <Text>{effectivePatientCount}</Text>
                          {effectivePatientCount > 0 && (
                            <Badge 
                              size="sm" 
                              colorScheme={shift.status === 'completed' ? 'green' : 'yellow'}
                              variant="subtle"
                            >
                              {shift.status === 'completed' ? 'Reports Complete' : 'In Progress'}
                            </Badge>
                          )}
                          {getShiftPhase(shift.shiftDate) === 'active' && (
                            <Badge size="sm" colorScheme="green" variant="outline">
                              Active Shift
                            </Badge>
                          )}
                        </HStack>
                      </Td>
                      {userRole === 'admin' && (
                        <Td border="none" color="gray.300">
                          {shift.institution}
                        </Td>
                      )}
                      {userRole === 'admin' && (
                        <Td border="none" color="gray.300">
                          {shift.cohort?.displayName || "—"}
                        </Td>
                      )}
                      <Td border="none">
                        {(() => {
                          const lockState = getShiftLockState(shift);
                          
                          let tooltipText;
                          if (userRole === 'student') {
                            tooltipText = 'Student lock controls are unavailable from this list';
                          } else if (userRole !== 'student') {
                            tooltipText = lockState.isLocked ? 'Unlock shift' : 'Lock shift';
                          } else {
                            tooltipText = lockState.reason;
                          }
                          
                          return (
                            <Tooltip label={tooltipText} hasArrow>
                              <IconButton
                                icon={lockState.isLocked ? <LockIcon /> : <UnlockIcon />}
                                size="sm"
                                colorScheme={lockState.isLocked ? 'red' : 'green'}
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleLock(shift);
                                }}
                                isDisabled={userRole === 'student' ? true : false}
                                isLoading={isUpdating}
                                aria-label={lockState.isLocked ? 'Unlock Shift' : 'Lock Shift'}
                              />
                            </Tooltip>
                          );
                        })()}
                      </Td>
                      <Td border="none">
                        <HStack spacing={1}>
                          <Tooltip label="Add Patient Encounter" hasArrow>
                            <IconButton
                              icon={<AddIcon />}
                              size="sm"
                              colorScheme="green"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddPatientEncounter(shift);
                              }}
                              isDisabled={!canAddPatientEncounter(shift)}
                              aria-label="Add Patient Encounter"
                            />
                          </Tooltip>
                          
                          <Tooltip label="View Patient List" hasArrow>
                            <IconButton
                              icon={<HamburgerIcon />}
                              size="sm"
                              colorScheme="blue"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewPatientList(shift);
                              }}
                              isDisabled={!canViewPatientList(shift)}
                              aria-label="View Patient List"
                            />
                          </Tooltip>
                          
                          <Tooltip label="Edit Shift / Request Edit" hasArrow>
                            <IconButton
                              icon={
                                <Box position="relative">
                                  <EditIcon />
                                  <QuestionIcon 
                                    position="absolute" 
                                    top="-2px" 
                                    right="-2px" 
                                    boxSize="8px"
                                    color="yellow.400"
                                  />
                                </Box>
                              }
                              size="sm"
                              colorScheme="yellow"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRequestEdit(shift);
                              }}
                              isDisabled={!canEditShift(shift)}
                              aria-label="Edit Shift"
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  )})}
                </Tbody>
              </Table>
            </Box>
            
            {filteredShifts?.length === 0 && (
              <Box p={8} textAlign="center">
                <Text color="gray.400" fontSize="lg">
                  No shifts found
                </Text>
                <Text color="gray.500" fontSize="sm" mt={2}>
                  {searchTerm || statusFilter ? 
                    'Try adjusting your search or filters.' : 
                    'Create your first shift to get started.'
                  }
                </Text>
              </Box>
            )}
          </CardBody>
        </Card>
      </VStack>

      {/* Unlock Request Modal */}
      <Modal isOpen={isOpen} onClose={handleCloseModal} size="md">
        <ModalOverlay />
        <ModalContent bg="gray.800" color="white">
          <ModalHeader>Request Shift Unlock</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text color="gray.300">
                You are requesting to unlock the shift scheduled for{' '}
                <Text as="span" fontWeight="bold" color="blue.300">
                  {selectedShift ? formatDate(selectedShift.shiftDate) : ''}
                </Text>
                {' '}at{' '}
                <Text as="span" fontWeight="bold" color="blue.300">
                  {selectedShift ? selectedShift.location : ''}
                </Text>
                .
              </Text>
              
              <Text color="gray.300">
                Please provide a reason for this unlock request:
              </Text>
              
              <Textarea
                placeholder="Enter your reason for requesting to unlock this shift..."
                value={unlockReason}
                onChange={(e) => setUnlockReason(e.target.value)}
                bg="gray.700"
                border="1px solid"
                borderColor="gray.600"
                color="white"
                _placeholder={{ color: 'gray.400' }}
                rows={4}
                resize="vertical"
              />
              
              <Text fontSize="sm" color="gray.400">
                Your instructor will review this request and may approve or deny it.
                You will be notified of their decision.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleSubmitUnlockRequest}
              isDisabled={!unlockReason.trim()}
            >
              Submit Request
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Shifts;
